// openSuggestionModal — opens the native creation modal pre-filled with
// AI-extracted suggestion data. Buckets that have a dedicated modal get
// routed there directly so the user gets the full editing experience.
// Others (notes, knowledge_base, chat_messages) fall back to
// MeetSuggestionConfirmModal.
//
// For calendar_events: opens the booking modal pre-filled with the
// suggestion data. No meet room is pre-created — the user fills in the
// meeting URL themselves, or the modal auto-generates a Google Meet link
// on save as it does for regular events.
//
// Sets root.pendingSuggestion = { bucket, item } before opening so the
// target modal's save handler can call markSuggestionApplied once the
// user confirms.

import { positionBetween } from '../../functions/tickets/ticketUtils.js'
import { freshBooking } from '../../functions/calendar/calendarUtils.js'

// Mirror of the helper in applySuggestion.js — duplicated to avoid
// importing across function modules (each registered fn is self-contained).
const _pad = (n) => String(n).padStart(2, '0')

const _parseProposedTime = (str) => {
  if (!str) return null
  const d = new Date(str)
  if (!isNaN(d)) {
    return {
      date: `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`,
      time: `${_pad(d.getHours())}:${_pad(d.getMinutes())}`
    }
  }
  return null
}

const _tomorrowAt10 = () => {
  const t = new Date()
  t.setDate(t.getDate() + 1)
  return {
    date: `${t.getFullYear()}-${_pad(t.getMonth() + 1)}-${_pad(t.getDate())}`,
    time: '10:00'
  }
}

export const openSuggestionModal = function (bucket, item) {
  const root = this.state?.root ?? this.state
  if (!root || !bucket || !item) return

  if (bucket === 'tickets') {
    // Auto-create the ticket immediately so the full TicketsDetailModal
    // view mode opens — same experience as clicking a ticket at /tickets.
    const colKey = 'backlog'
    const sameCol = (root.tickets || []).filter((t) => t.columnKey === colKey)
    const lastPos = sameCol.length
      ? Math.max(...sameCol.map((t) => t.position || 0))
      : 0
    const t = {
      id: Date.now(),
      title: item.title || '',
      body: item.description || '',
      columnKey: colKey,
      position: positionBetween(lastPos, null),
      assigneeEmail: item.assignee_email || null,
      reporterEmail: root.currentUserEmail,
      labels: [],
      priority: 'medium',
      dueDate: null
    }
    root.update({
      tickets: [...(root.tickets || []), t],
      selectedTicket: t,
      pendingSuggestion: { bucket, item }
    })
    const self = this
    self.call('saveTicketToSupabase', t).then(function (realId) {
      if (!realId) return
      const updated = Object.assign({}, t, { id: realId })
      const stillExists = (root.tickets || []).some((x) => x.id === t.id)
      root.update({
        tickets: (root.tickets || []).map((x) => (x.id === t.id ? updated : x)),
        selectedTicket: stillExists ? updated : root.selectedTicket,
        pendingSuggestion: null
      })
      self.call('markSuggestionApplied', bucket, item, realId)
    })
    return
  }

  if (bucket === 'calendar_events') {
    const { date, time } = _parseProposedTime(item.proposed_time) || _tomorrowAt10()
    const [h, m] = time.split(':').map(Number)
    const endH = String((h + 1) % 24).padStart(2, '0')
    const endTime = `${endH}:${String(m).padStart(2, '0')}`

    root.update({
      newBooking: freshBooking({
        title: item.title || '',
        date,
        startTime: time,
        endTime,
        description: item.description || '',
        invitees: (item.attendees || []).filter(Boolean).join(', '),
        type: 'meeting',
        meetingMode: 'symbols-meet'
      }),
      showNewBooking: true,
      pendingSuggestion: { bucket, item }
    })
    return
  }

  // Fallback: generic confirm modal (notes, knowledge_base, chat_messages).
  const initTitle = bucket === 'chat_messages'
    ? (item?.body || '')
    : (item?.title || item?.body || '')
  root.update({ suggestionConfirm: { bucket, item }, suggestionConfirmTitle: initTitle })
}
